// src/components/Events/Tree/TreePage.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Paper,
    Stack,
    Typography,
    CircularProgress,
    Alert,
    Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
    DndContext,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';

import api from '../../../api/axios';
import { useUser } from '../../../contexts/UserContext';

import TreeCanvas from './TreeCanvas';
import TreeItemPalette from './TreeItemPalette';
import TreeMessageDialog from './TreeMessageDialog';


// Django media 경로 보정 (TreeCanvas랑 동일)
function resolveMediaUrl(url) {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return url; // 보통 /media/... 형태
}

// ✅ 고정 씬 비율 (배경 원본 1600x1200 = 4:3) - 기존 로직 유지용
const SCENE_AR = 4 / 3;

// ✅ 어디든 둘 수 있게 범위 확장 (밖으로도 배치 허용)
const clampRange = (v, min = -0.3, max = 1.3) => Math.max(min, Math.min(max, v));

const getSceneRect = (w, h) => {
    if (!w || !h) return { x: 0, y: 0, w: 0, h: 0 };

    const boxAR = w / h;
    let sceneW, sceneH;

    if (boxAR > SCENE_AR) {
        sceneH = h;
        sceneW = h * SCENE_AR;
    } else {
        sceneW = w;
        sceneH = w / SCENE_AR;
    }

    return {
        x: (w - sceneW) / 2,
        y: (h - sceneH) / 2,
        w: sceneW,
        h: sceneH,
    };
};

// ✅ 드래그 중인 요소(active)의 "현재" 중심점 (뷰포트 기준)
const getDropPointFromActive = (active) => {
    const r = active?.rect?.current?.translated || active?.rect?.current?.initial;
    if (!r) return null;
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
};

// ✅ fallback용 포인터 좌표
const getClientXY = (e) => {
    if (!e) return null;

    if (typeof e.clientX === 'number' && typeof e.clientY === 'number') {
        return { x: e.clientX, y: e.clientY };
    }

    const t = e.changedTouches?.[0] || e.touches?.[0];
    if (t) return { x: t.clientX, y: t.clientY };

    return null;
};

// ✅ (추가) pointer 좌표 얻기(모바일/PC 공통)
const getEventClientXY = (e) => {
    if (!e) return null;

    if (typeof e.clientX === 'number' && typeof e.clientY === 'number') {
        return { x: e.clientX, y: e.clientY };
    }

    const t = e.changedTouches?.[0] || e.touches?.[0];
    if (t) return { x: t.clientX, y: t.clientY };

    return null;
};

// ✅ object-fit: cover 매핑(기본 objectPosition: 50% 50%)
const getCoverMap = (boxW, boxH, imgW, imgH) => {
    if (!boxW || !boxH || !imgW || !imgH) return null;

    const scale = Math.max(boxW / imgW, boxH / imgH);
    const drawW = imgW * scale;
    const drawH = imgH * scale;

    // 중앙 정렬(50% 50%)
    const ox = (boxW - drawW) / 2;
    const oy = (boxH - drawH) / 2;

    return { scale, drawW, drawH, ox, oy };
};

// ✅ client 좌표 → "배경 이미지" 좌표(0~1)로 역변환 (cover 크롭 반영)
const clientPointToImage01 = (canvasEl, point, bgNatural) => {
    if (!canvasEl || !point || !bgNatural?.w || !bgNatural?.h) return null;

    const rect = canvasEl.getBoundingClientRect();
    const boxW = canvasEl.clientWidth;
    const boxH = canvasEl.clientHeight;

    const map = getCoverMap(boxW, boxH, bgNatural.w, bgNatural.h);
    if (!map) return null;

    const localX = point.x - rect.left;
    const localY = point.y - rect.top;

    const x01 = (localX - map.ox) / map.drawW;
    const y01 = (localY - map.oy) / map.drawH;

    return { x: clampRange(x01), y: clampRange(y01) };
};

export default function TreePage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { userInfo, isLoggedIn } = useUser();
    const loggedIn = useMemo(
        () => (typeof isLoggedIn === 'boolean' ? isLoggedIn : !!userInfo),
        [isLoggedIn, userInfo]
    );

    const canvasRef = useRef(null);
    const lastDragPointRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isReplaceMode, setIsReplaceMode] = useState(false);

    const [activeEvent, setActiveEvent] = useState(null);
    const [items, setItems] = useState([]);
    const [decorations, setDecorations] = useState([]);
    const [me, setMe] = useState({ has_decorated: false, decoration: null });

    const [pendingPreview, setPendingPreview] = useState(null);
    const [pendingDecorate, setPendingDecorate] = useState(null);
    // ✅ 문구 수정용
    const [editingDeco, setEditingDeco] = useState(null); // decoration 객체

    // ✅ cover 기준 좌표계에 필요한 배경 원본 크기
    const [bgNatural, setBgNatural] = useState(null);

    const eventId = activeEvent?.id;
    const myDecorationId = me?.decoration?.id || null;

    const parseDecoDragId = (id) => {
        const s = String(id || '');
        if (!s.startsWith('deco:')) return null;
        const n = Number(s.split(':')[1]);
        return Number.isFinite(n) ? n : null;
    };

    // ✅ 모바일: 탭(클릭) vs 드래그 구분
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 6 },
        }),
        useSensor(TouchSensor, {
            activationConstraint: { delay: 180, tolerance: 6 },
        })
    );

    const fetchAll = async () => {
        setLoading(true);
        setError('');
        try {
            const evRes = await api.get('/api/events/tree/active/');
            const ev = evRes.data;

            if (!ev) {
                setActiveEvent(null);
                setItems([]);
                setDecorations([]);
                setMe({ has_decorated: false, decoration: null });
                setPendingDecorate(null);
                setPendingPreview(null);
                return;
            }

            setActiveEvent(ev);

            const [itemsRes, decoRes] = await Promise.all([
                api.get('/api/events/tree/items/', { params: { event: ev.id } }),
                api.get('/api/events/tree/decorations/', { params: { event: ev.id } }),
            ]);

            setItems(itemsRes.data || []);
            setDecorations(decoRes.data || []);

            if (loggedIn) {
                const meRes = await api.get('/api/events/tree/me/', { params: { event: ev.id } });
                setMe(meRes.data || { has_decorated: false, decoration: null });
            } else {
                setMe({ has_decorated: false, decoration: null });
            }
        } catch (err) {
            console.error(err);
            setError('트리 정보를 불러오는 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
            setIsReplaceMode(false);
        }
    };

    useEffect(() => {
        fetchAll();
        setIsReplaceMode(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loggedIn]);

    // ✅ TreeCanvas에서 window.dispatchEvent로 쏘는 "문구 수정" 이벤트 받기
    useEffect(() => {
        const handler = (e) => {
            const deco = e?.detail;
            if (!deco) return;

            // 내 장식만 허용(안전장치)
            if (myDecorationId && deco.id !== myDecorationId) return;

            setEditingDeco(deco);
        };

        window.addEventListener('tree-edit-message', handler);
        return () => window.removeEventListener('tree-edit-message', handler);
    }, [myDecorationId]);

    // ✅ 배경 이미지 natural size 로드 (cover 좌표 변환용)
    useEffect(() => {
        const url = activeEvent?.bg_image;
        if (!url) {
            setBgNatural(null);
            return;
        }

        const img = new Image();
        img.onload = () => {
            setBgNatural({ w: img.naturalWidth, h: img.naturalHeight });
        };
        img.onerror = () => setBgNatural(null);

        // bg_image가 보통 /media/... 라면 그대로 OK (resolveMediaUrl 쓰면 더 안전)
        img.src = resolveMediaUrl(url);
    }, [activeEvent?.bg_image]);

    const normalizeDeco = (resData) => resData?.decoration ?? resData;

    const handleDecorateSubmit = async (payload) => {
        setError('');
        try {
            const res = await api.post('/api/events/tree/decorate/', payload);
            const deco = normalizeDeco(res.data);

            setMe({ has_decorated: true, decoration: deco });
            setDecorations((prev) =>
                prev.some((d) => d.id === deco.id)
                    ? prev.map((d) => (d.id === deco.id ? deco : d))
                    : [...prev, deco]
            );
        } catch (err) {
            console.error(err);
            setError(err?.response?.data?.error || '저장 중 오류가 발생했습니다.');
        }
    };

    // ✅ 내 장식 드래그 이동 저장 (기존 delta 기반 유지: 다음 단계에서 cover좌표로 통일할 것)
    // ✅ 내 장식 드래그 이동 저장 (cover 좌표계 기준)
    const saveMove = async ({ decoId, delta }) => {
        const myDeco = decorations.find((d) => d.id === decoId);
        const canvasEl = canvasRef.current;
        if (!myDeco || !canvasEl) return;

        const w = canvasEl.clientWidth;
        const h = canvasEl.clientHeight;
        if (!w || !h) return;

        const currX01 = clampRange(Number(myDeco.x) || 0);
        const currY01 = clampRange(Number(myDeco.y) || 0);

        // ✅ coverMap이 가능하면 cover 기준으로 px 이동 후 다시 01로 환산
        if (bgNatural?.w && bgNatural?.h) {
            const map = getCoverMap(w, h, bgNatural.w, bgNatural.h);
            if (!map) return;

            const currPxX = map.ox + map.drawW * currX01;
            const currPxY = map.oy + map.drawH * currY01;

            const nextPxX = currPxX + (delta?.x || 0);
            const nextPxY = currPxY + (delta?.y || 0);

            const nextX = clampRange((nextPxX - map.ox) / map.drawW);
            const nextY = clampRange((nextPxY - map.oy) / map.drawH);

            if (
                Math.abs(nextX - (Number(myDeco.x) || 0)) < 0.0005 &&
                Math.abs(nextY - (Number(myDeco.y) || 0)) < 0.0005
            ) {
                return;
            }

            const res = await api.post('/api/events/tree/decorate/', {
                event: eventId,
                item: myDeco.item?.id,
                x: nextX,
                y: nextY,
                scale: myDeco.scale ?? 1,
                rotate: myDeco.rotate ?? 0,
                z_index: myDeco.z_index ?? 1,
                message: myDeco.message ?? '',
            });

            const updated = normalizeDeco(res.data);
            setMe({ has_decorated: true, decoration: updated });
            setDecorations((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
            return;
        }

        // ✅ fallback: bgNatural 없으면 기존 scene 로직 유지
        const scene = getSceneRect(w, h);
        const currPxX = scene.x + currX01 * scene.w;
        const currPxY = scene.y + currY01 * scene.h;

        const nextPxX = currPxX + (delta?.x || 0);
        const nextPxY = currPxY + (delta?.y || 0);

        const nextX = clampRange((nextPxX - scene.x) / scene.w);
        const nextY = clampRange((nextPxY - scene.y) / scene.h);

        const res = await api.post('/api/events/tree/decorate/', {
            event: eventId,
            item: myDeco.item?.id,
            x: nextX,
            y: nextY,
            scale: myDeco.scale ?? 1,
            rotate: myDeco.rotate ?? 0,
            z_index: myDeco.z_index ?? 1,
            message: myDeco.message ?? '',
        });

        const updated = normalizeDeco(res.data);
        setMe({ has_decorated: true, decoration: updated });
        setDecorations((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
    };

    // ✅ 드롭 시 좌표 계산
    const handleDragEnd = async ({ active, over, delta, activatorEvent }) => {
        if (!over || over.id !== 'tree-drop-zone') {
            setPendingPreview(null);
            return;
        }
        if (!eventId || !loggedIn) return;

        // (1) 내 장식 이동이면 saveMove
        const decoId = parseDecoDragId(active?.id);
        if (decoId && decoId === myDecorationId) {
            await saveMove({ decoId, delta });
            return;
        }

        // (2) 교체 모드 처리
        if (me?.has_decorated) {
            if (!isReplaceMode) return;

            const item = active?.data?.current?.item;
            const myDeco = me?.decoration;
            if (!item || !myDeco) return;

            try {
                const res = await api.post('/api/events/tree/decorate/', {
                    event: eventId,
                    item: item.id,
                    x: myDeco.x,
                    y: myDeco.y,
                    scale: myDeco.scale ?? 1,
                    rotate: myDeco.rotate ?? 0,
                    z_index: myDeco.z_index ?? 1,
                    message: myDeco.message ?? '',
                });

                const updated = normalizeDeco(res.data);
                setMe({ has_decorated: true, decoration: updated });
                setDecorations((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
                setIsReplaceMode(false);
            } catch (err) {
                console.error(err);
                setError(err?.response?.data?.error || '아이템 교체 중 오류가 발생했습니다.');
            }
            return;
        }

        // (3) 신규 장식 드롭 좌표 계산
        const item = active?.data?.current?.item;
        if (!item) return;

        const canvasEl = canvasRef.current;
        if (!canvasEl) return;

        // ✅ 1순위: active 중심점
        // ✅ 2순위: activatorEvent
        const p = getDropPointFromActive(active) || getClientXY(activatorEvent);
        if (!p) return;

        // ✅ cover 기준 좌표(=배경 이미지 좌표)로 저장하고 싶으면 bgNatural이 필요
        // bgNatural이 아직 없으면 기존(scene) 방식으로 fallback
        if (bgNatural?.w && bgNatural?.h) {
            const xy = clientPointToImage01(canvasEl, p, bgNatural);
            if (!xy) return;

            setPendingPreview({ x: xy.x, y: xy.y, item });
            setPendingDecorate({ event: eventId, item: item.id, x: xy.x, y: xy.y });
            return;
        }

        // fallback: 기존 scene 기준
        const rect = canvasEl.getBoundingClientRect();
        const w = canvasEl.clientWidth;
        const h = canvasEl.clientHeight;
        if (!w || !h) return;

        const scene = getSceneRect(w, h);
        const x = clampRange((p.x - rect.left - scene.x) / scene.w);
        const y = clampRange((p.y - rect.top - scene.y) / scene.h);

        setPendingPreview({ x, y, item });
        setPendingDecorate({ event: eventId, item: item.id, x, y });
    };

    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                <Stack alignItems="center" spacing={2}>
                    <CircularProgress />
                    <Typography variant="body2">트리 불러오는 중…</Typography>
                </Stack>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                p: 2,
                width: '100%',
                maxWidth: '100vw',
                overflowX: 'hidden',
                boxSizing: 'border-box',
            }}
        >
            <DndContext
                sensors={sensors}
                onDragStart={({ activatorEvent, active }) => {
                    lastDragPointRef.current =
                        getEventClientXY(activatorEvent) ||
                        getDropPointFromActive(active) ||
                        null;
                }}
                onDragMove={({ activatorEvent, active }) => {
                    const p =
                        getEventClientXY(activatorEvent) ||
                        getDropPointFromActive(active);

                    if (p) lastDragPointRef.current = p;
                }}
                onDragCancel={() => {
                    lastDragPointRef.current = null;
                }}
                onDragEnd={(e) => {
                    handleDragEnd(e);
                    lastDragPointRef.current = null;
                }}
            >
                <Stack spacing={2}>
                    {/* 헤더 */}
                    <Paper
                        sx={{
                            p: 2,
                            borderRadius: 3,
                            position: 'relative',
                            overflow: 'hidden',
                            backdropFilter: 'blur(8px)',
                            background:
                                'linear-gradient(135deg, rgba(255,255,255,0.85), rgba(255,255,255,0.65))',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.10)',
                            border: '1px solid rgba(255,255,255,0.7)',
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                pointerEvents: 'none',
                                opacity: 0.9,
                                background: `
                  radial-gradient(circle at 15% 20%, rgba(255,0,0,0.18), transparent 42%),
                  radial-gradient(circle at 85% 30%, rgba(0,160,80,0.18), transparent 45%),
                  radial-gradient(circle at 60% 90%, rgba(255,215,0,0.16), transparent 45%),
                  linear-gradient(90deg, rgba(255,255,255,0.00), rgba(255,255,255,0.35), rgba(255,255,255,0.00))
                `,
                            }}
                        />

                        <Box
                            sx={{
                                position: 'absolute',
                                left: -20,
                                top: 12,
                                width: 220,
                                height: 10,
                                borderRadius: 999,
                                transform: 'rotate(-8deg)',
                                background:
                                    'linear-gradient(90deg, rgba(220,20,60,0.85), rgba(255,105,180,0.65))',
                                boxShadow: '0 6px 18px rgba(220,20,60,0.18)',
                                pointerEvents: 'none',
                            }}
                        />

                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            sx={{ position: 'relative' }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.4 }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 900,
                                            letterSpacing: '-0.02em',
                                            lineHeight: 1.1,
                                            background:
                                                'linear-gradient(90deg, rgba(200,0,0,0.95), rgba(0,120,60,0.95))',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            minWidth: 0,
                                        }}
                                    >
                                        {activeEvent?.name}
                                    </Typography>
                                </Stack>

                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.8,
                                        px: 1.2,
                                        py: 0.6,
                                        borderRadius: 999,
                                        backgroundColor: 'rgba(255,255,255,0.75)',
                                        border: '1px solid rgba(0,0,0,0.06)',
                                        boxShadow: '0 6px 14px rgba(0,0,0,0.06)',
                                        maxWidth: '100%',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: 999,
                                            backgroundColor: me?.has_decorated
                                                ? 'rgba(0,160,80,0.9)'
                                                : 'rgba(220,20,60,0.9)',
                                            boxShadow: '0 0 0 4px rgba(255,255,255,0.9)',
                                            flex: '0 0 auto',
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            fontWeight: 700,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: { xs: '100%', sm: 520 },
                                        }}
                                    >
                                        {me?.has_decorated
                                            ? isReplaceMode
                                                ? '교체할 아이템을 선택해 트리에 드롭하세요'
                                                : '🎄 꾸미기 완료 🎄'
                                            : '아이템을 끌어 트리를 꾸며보세요'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Stack
                                direction={{ xs: 'row', sm: 'column' }}
                                spacing={1}
                                sx={{
                                    width: { xs: '100%', sm: 'auto' },
                                    alignItems: { xs: 'stretch', sm: 'flex-end' },
                                }}
                            >
                                <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                                    {loggedIn && me?.has_decorated && (
                                        <Button
                                            variant="outlined"
                                            color={isReplaceMode ? 'warning' : 'primary'}
                                            onClick={() => setIsReplaceMode((v) => !v)}
                                            sx={{
                                                whiteSpace: 'nowrap',
                                                minWidth: 120,
                                                borderRadius: 999,
                                                fontWeight: 800,
                                                backgroundColor: 'rgba(255,255,255,0.65)',
                                            }}
                                        >
                                            {isReplaceMode ? '교체 취소' : '아이템 교체'}
                                        </Button>
                                    )}

                                    <Button
                                        variant="contained"
                                        onClick={fetchAll}
                                        sx={{
                                            whiteSpace: 'nowrap',
                                            minWidth: 120,
                                            borderRadius: 999,
                                            fontWeight: 900,
                                            boxShadow: '0 10px 22px rgba(0,0,0,0.14)',
                                        }}
                                    >
                                        새로고침
                                    </Button>
                                </Stack>
                            </Stack>
                        </Stack>

                        {error && (
                            <Alert severity="warning" sx={{ mt: 2, position: 'relative' }}>
                                {error}
                            </Alert>
                        )}
                    </Paper>

                    {/* 본문 */}
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ overflowX: 'hidden' }}>
                        <Box sx={{ flex: 1, overflow: 'hidden' }}>
                            <TreeCanvas
                                event={activeEvent}
                                decorations={decorations}
                                pendingPreview={pendingPreview}
                                height={isMobile ? undefined : 680}
                                outerRef={canvasRef}
                                dropDisabled={!loggedIn}
                                myDecorationId={myDecorationId}
                                bgNatural={bgNatural} // ✅ cover 좌표계용
                            />
                        </Box>

                        <Box sx={{ width: { xs: '100%', md: 340 } }}>
                            <TreeItemPalette
                                items={items}
                                disabled={!loggedIn || (me?.has_decorated && !isReplaceMode)}
                                compact={isMobile}
                            />
                        </Box>
                    </Stack>
                </Stack>
            </DndContext>

            <TreeMessageDialog
                open={!!pendingDecorate}
                onClose={() => {
                    setPendingDecorate(null);
                    setPendingPreview(null);
                }}
                onSubmit={(message) => {
                    handleDecorateSubmit({ ...pendingDecorate, message });
                    setPendingDecorate(null);
                    setPendingPreview(null);
                }}
            />

            {/* ✅ 문구 수정 다이얼로그 */}
            <TreeMessageDialog
                open={!!editingDeco}
                title="문구 수정"
                submitText="수정하기"
                initialValue={editingDeco?.message || ''}
                onClose={() => setEditingDeco(null)}
                onSubmit={(message) => {
                    if (!editingDeco) return;
                    if (!eventId) return; // ✅ 안전

                    // 기존 장식 값 유지 + message만 교체해서 decorate API 재호출(업데이트)
                    handleDecorateSubmit({
                        event: eventId,
                        item: editingDeco.item?.id,
                        x: editingDeco.x,
                        y: editingDeco.y,
                        scale: editingDeco.scale ?? 1,
                        rotate: editingDeco.rotate ?? 0,
                        z_index: editingDeco.z_index ?? 1,
                        message,
                    });

                    setEditingDeco(null);
                }}
            />
        </Box>
    );
}
